import { BADGE_VARIANTS, type BadgeVariant } from '../../../components/ui/badgeVariants';
import { isYesCode } from '../../../lib/boolCode';
import theme from '../../../theme/theme';
import type { Payslip } from '../payslipTypes';

/**
 * Presentation mapping for the payslips table — kept out of the table component
 * and expressed as *semantic* variants (palette-free), so the colours live once in
 * {@link BADGE_VARIANTS}. Type colour is a single origin shared by the type badge
 * and the row text, so the two can never drift apart.
 */

export type PayslipTypeKey = 'payslip' | 'stc';
export type PayslipClosureUiKey = 'open' | 'provisional' | 'closed';

/** STC stands out (primary/blue); a classic payslip is neutral/grey. */
export const PAYSLIP_TYPE_VARIANT: Record<PayslipTypeKey, BadgeVariant> = {
  payslip: 'neutral',
  stc: 'primary',
};

/** Open = neutral, Provisional = warning (yellow), Closed = success (green). */
export const PAYSLIP_CLOSURE_VARIANT: Record<PayslipClosureUiKey, BadgeVariant> = {
  open: 'neutral',
  provisional: 'warning',
  closed: 'success',
};

/** `soldetout_compte` → type key ('O' = STC, else payslip). */
export const payslipTypeKey = (payslip: Payslip): PayslipTypeKey =>
  isYesCode(payslip.soldetout_compte) ? 'stc' : 'payslip';

/**
 * Row font colour by payslip type: STC keeps the accent (blue, same origin as the
 * type badge); a classic payslip uses the standard dark body text (`text.body`,
 * near-black) rather than the neutral grey, so the list reads black, not grey.
 */
export const rowTextColor = (payslip: Payslip): string =>
  payslipTypeKey(payslip) === 'stc'
    ? BADGE_VARIANTS[PAYSLIP_TYPE_VARIANT.stc].color
    : theme.palette.text.body;
